/**
 * Idempotenz-Guard: verhindert, dass derselbe Import mehrfach gespeichert wird.
 *
 * Hintergrund: Beim Import aus Rechargy wurden dieselben Buchungen mehrfach
 * angelegt (522 echte Kopien, 3.187,68 € Anteil). Appwrite kennt keinen
 * Unique-Constraint auf (Kunde, Tag) - der Guard sitzt deshalb in der App.
 *
 * WICHTIG: Die Identität ist NICHT "Kunde + Tag". Am selben Tag können zwei
 * echte, unterschiedliche Buchungen existieren (z. B. 4,50 € neben 25,88 € bei
 * Cafe Bar Celona). Deshalb zählt zusätzlich der Betrag UND die Beschreibung.
 */

/** Betrag auf zwei Nachkommastellen normalisieren (auch Komma-Zahlweise). */
export const normalizeAmount = (value) => {
  const parsed = parseFloat(String(value ?? '').replace(',', '.'));
  if (!Number.isFinite(parsed)) return 0;
  return Math.round(parsed * 100) / 100;
};

/** Datum auf den Tag normalisieren (ISO-Strings, Timestamps). */
export const normalizeDay = (value) => {
  if (!value) return '';
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return text.slice(0, 10);
  return parsed.toISOString().slice(0, 10);
};

/** Text für den Vergleich normalisieren (Groß-/Kleinschreibung, Trim). */
export const normalizeText = (value) => String(value ?? '').trim().toLowerCase();

const creditCustomer = (entry) => entry?.customer_id ?? entry?.customerId ?? '';
// Betrag/Datum koennen aus dem Formular (revenueAmount/date) oder aus der DB
// (revenue_amount/transfer_date) kommen - beide Schreibweisen beruecksichtigen.
const creditAmount = (entry) => entry?.revenue_amount ?? entry?.revenueAmount ?? entry?.amount;
const creditDate = (entry) => entry?.transfer_date ?? entry?.date ?? entry?.period_start ?? '';
const expenseTitle = (entry) => entry?.title ?? entry?.description;

/**
 * Prüft, ob eine Gutschrift/Umsatzbuchung bereits vorhanden ist.
 * @param {Array<object>} existing  bestehende Buchungen
 * @param {object} candidate  neue Buchung
 */
export const isDuplicateCredit = (existing, candidate) => {
  const list = Array.isArray(existing) ? existing : [];
  const cCustomer = creditCustomer(candidate);
  const cDay = normalizeDay(creditDate(candidate));
  const cAmount = normalizeAmount(creditAmount(candidate));
  const cText = normalizeText(candidate?.description);

  return list.some((entry) => {
    if (creditCustomer(entry) !== cCustomer) return false;
    if (normalizeDay(creditDate(entry)) !== cDay) return false;
    if (normalizeAmount(creditAmount(entry)) !== cAmount) return false;
    if (normalizeText(entry?.description) !== cText) return false;
    return true;
  });
};

/**
 * Prüft, ob eine Ausgabe bereits vorhanden ist.
 * Identität: Titel + Betrag + Datum + Typ.
 */
export const isDuplicateExpense = (existing, candidate) => {
  const list = Array.isArray(existing) ? existing : [];
  const cTitle = normalizeText(expenseTitle(candidate));
  const cAmount = normalizeAmount(candidate?.amount);
  const cDay = normalizeDay(candidate?.date);
  const cType = normalizeText(candidate?.type) || 'expense';

  return list.some((entry) => {
    if (normalizeText(expenseTitle(entry)) !== cTitle) return false;
    if (normalizeAmount(entry?.amount) !== cAmount) return false;
    if (normalizeDay(entry?.date ?? entry?.transfer_date) !== cDay) return false;
    if ((normalizeText(entry?.type) || 'expense') !== cType) return false;
    return true;
  });
};

/** Verständliche Meldung für die Oberfläche. */
export const DUPLICATE_CREDIT_MESSAGE =
  'Diese Buchung existiert bereits (gleicher Kunde, Tag, Betrag und Beschreibung). Bitte prüfe den vorhandenen Eintrag, bevor du erneut importierst.';

export const DUPLICATE_EXPENSE_MESSAGE =
  'Diese Ausgabe existiert bereits (gleicher Titel, Betrag und Datum). Bitte prüfe den vorhandenen Eintrag.';

export default {
  normalizeAmount,
  normalizeDay,
  normalizeText,
  isDuplicateCredit,
  isDuplicateExpense,
  DUPLICATE_CREDIT_MESSAGE,
  DUPLICATE_EXPENSE_MESSAGE,
};
